import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { Spinner } from './components/ui/Spinner';
import { useAuthStore } from './store/authStore';
import { Layout } from './components/layout/Layout';
import { B2BLayout } from './components/layout/B2BLayout';
import BulkOrdersPage from './pages/orders/bulk-orders';
import { ROUTES } from './constants/routes';
import RateCalculator from './pages/rate-calculator';
import SettingChannel from './pages/settings/channel';
import SettingWarehouse from './pages/settings/warehouse';
import SettingTransporterId from './pages/settings/transport-id';
import SettingPincode from './pages/settings/pincode';
import RemittancePage from './pages/finance/remittance';
import InvoicesPage from './pages/finance/invoices';
import NDRPage from './pages/ndr';
import AddWarehousePage from './pages/settings/warehouse/add';
import AllOrdersPage from './pages/orders';
import RateCardPage from './pages/rate-card';
import RemittanceDetailsPage from './pages/finance/remittance/details';
import InvoiceDetailsPage from './pages/finance/invoices/details';
import ManageUsersPage from './pages/admin/manage-users';
import UploadPincode from './pages/admin/upload-pincode';
import WalletsPage from './pages/finance/wallet';
import WeightDiscrepancyPage from './pages/weight';
import ManageLabel from './pages/settings/label';
import SupportPage from './pages/support';
import ReportsPage from './pages/reports';
import RecoBillsAdminPage from './pages/admin/reco-bills';
import InwardRemittancePage from './pages/admin/inward-remittance';
import OutwardRemittancePage from './pages/admin/outward-remittance';
import ShipmentChargesAdminPage from './pages/admin/shipment-charges';
import AdminWalletTransactionPage from './pages/admin/wallet-transaction';
import AdminRatesPage from './pages/admin/rates';
import UserRatesPage from './pages/admin/manage-users/user-rates';

// Lazy load pages with default imports
const LoginPage = lazy(() => import('./pages/Login/LoginPage'));
const SSOLoginPage = lazy(() => import('./pages/Login/SSOLoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword/ResetPasswordPage'));
const SignupPage = lazy(() => import('./pages/Signup/SignupPage'));
const DashboardPage = lazy(() => import('./pages/Dashboard/DashboardPage'));
const AddOrderPage = lazy(() => import('./pages/orders/add-order'));
const SettingsPage = lazy(() => import('./pages/settings'));
const B2bRatesPage = lazy(() => import('./pages/b2b/rates'));
const NotFoundPage = lazy(() => import('./pages/NotFound'));
const SettingProfilePage = lazy(() => import('./pages/settings/profile/index'));
const SettingLoginSecurity = lazy(() => import('./pages/settings/security'));
const SettingManageCourier = lazy(
  () => import('./pages/settings/manage-courier')
);
// B2B Pages
const B2BDashboardPage = lazy(() => import('./pages/b2b/DashboardPage'));
const B2BAddOrderPage = lazy(() => import('./pages/b2b/orders/add-order'));
const B2BAllOrdersPage = lazy(() => import('./pages/b2b/orders/index'));
const B2BOrderDetailsPage = lazy(() => import('./pages/b2b/orders/order-details'));
const B2BAppointmentPage = lazy(() => import('./pages/b2b/AppointmentPage'));
const B2BRateCardsPage = lazy(() => import('./pages/b2b/rate-cards'));
const B2BWalletsPage = lazy(() => import('./pages/b2b/finance/wallet/index'));
const B2BRateCalculatorPage = lazy(() => import('./pages/b2b/tools/rate-calculator').then(m => ({ default: m.B2bRateCalculator })));

// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Spinner size="lg" />
  </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading, authChecked } = useAuth();

  if (loading || !authChecked) {
    return <PageLoader />;
  }
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, authChecked } = useAuth();

  if (loading || !authChecked) {
    return <PageLoader />;
  }

  return !isAuthenticated ? (
    <>{children}</>
  ) : (
    <Navigate to="/dashboard" replace />
  );
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading, authChecked } = useAuth();

  useEffect(() => {
    if (authChecked && isAuthenticated && user?.roleName !== 'ADMIN' && user?.roleName !== 'SUPER_ADMIN') {
      toast.error('You do not have Admin privileges');
    }
  }, [authChecked, isAuthenticated, user]);

  if (loading || !authChecked) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.roleName !== 'ADMIN' && user?.roleName !== 'SUPER_ADMIN') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export const AppRouter: React.FC = () => {
  useEffect(() => {
    const handleGlobalLogout = () => useAuthStore.getState().logout();
    window.addEventListener('auth:logout', handleGlobalLogout);
    return () => window.removeEventListener('auth:logout', handleGlobalLogout);
  }, []);

  // Mark auth as checked once rehydration (or initial load) has had a chance to run.
  // Prevents infinite loader when persist rehydration is async.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      useAuthStore.getState().setAuthChecked(true);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // --- SHOPIFY HASH-BASED SSO REDIRECT ---
  // The Shopify app redirects to: ship.parcelpicks.com/#/sso-login?token=xxx&userId=yyy
  // We must intercept this BEFORE BrowserRouter renders, otherwise it matches '/' (login page).
  if (typeof window !== 'undefined' && window.location.hash.startsWith('#/sso-login')) {
    const cleanPath = window.location.hash.substring(1); // removes the '#'
    window.location.replace(cleanPath);
    return null; // Stop rendering until the browser reloads with the clean path
  }

  return (
    <BrowserRouter>
      <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Suspense fallback={<PageLoader />}>
                  <LoginPage />
                </Suspense>
              </PublicRoute>
            }
          />
          <Route
            path="/sso-login"
            element={
              <PublicRoute>
                <Suspense fallback={<PageLoader />}>
                  <SSOLoginPage />
                </Suspense>
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <Suspense fallback={<PageLoader />}>
                  <SignupPage />
                </Suspense>
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <Suspense fallback={<PageLoader />}>
                  <ResetPasswordPage />
                </Suspense>
              </PublicRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/b2b/dashboard"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BDashboardPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/orders"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BAllOrdersPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/orders/:id"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BOrderDetailsPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/orders/add"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BAddOrderPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/appointment"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BAppointmentPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/finance/wallet"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BWalletsPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/wallet"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BWalletsPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/admin/wallet-transaction"
            element={
              <AdminRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <AdminWalletTransactionPage />
                  </Suspense>
                </B2BLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/b2b/rate-cards"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BRateCardsPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/b2b/tools/rate-calculator"
            element={
              <ProtectedRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BRateCalculatorPage />
                  </Suspense>
                </B2BLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.B2B_RATES}
            element={
              <AdminRoute>
                <B2BLayout>
                  <Suspense fallback={<PageLoader />}>
                    <B2bRatesPage />
                  </Suspense>
                </B2BLayout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.ORDERS_APPOINTMENT}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <B2BAppointmentPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <DashboardPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.ORDERS}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <AllOrdersPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.RATE_CARD}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <RateCardPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ADD_ORDER}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <AddOrderPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/add/bulk"
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <BulkOrdersPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingsPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.RATE_CALCULATOR}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <RateCalculator />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REMITTANCE}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <RemittancePage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.REMITTANCE_DETAILS}
            element={
              <ProtectedRoute>
                <Layout>
                  <RemittanceDetailsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.NDR}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <NDRPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.INVOICE}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <InvoicesPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.WALLET}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <WalletsPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.WEIGHT}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <WeightDiscrepancyPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.INVOICE_DETAILS}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <InvoiceDetailsPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_PROFILE}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingProfilePage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_LABEL}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <ManageLabel />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_SECURITY}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingLoginSecurity />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_MANAGE}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingManageCourier />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_CHANNEL}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingChannel />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_WAREHOUSE}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingWarehouse />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_WAREHOUSE_ADD}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <AddWarehousePage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_TRANSPORT_ID}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingTransporterId />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SETTINGS_PINCODE}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SettingPincode />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MANAGE_USERS}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <ManageUsersPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.UPLOAD_PINCODE}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <UploadPincode />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.SUPPORT}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <SupportPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.RECO_BILLS}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <RecoBillsAdminPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.INWARD_REMITTANCE}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <InwardRemittancePage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.OUTWARD_REMITTANCE}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <OutwardRemittancePage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN_SHIPMENT_CHARGES}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <ShipmentChargesAdminPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN_WALLET_TRANSACTION}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <AdminWalletTransactionPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.REPORT}
            element={
              <ProtectedRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <ReportsPage />
                  </Suspense>
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN_RATES}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <AdminRatesPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />
          <Route
            path={ROUTES.ADMIN_USER_RATES}
            element={
              <AdminRoute>
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                    <UserRatesPage />
                  </Suspense>
                </Layout>
              </AdminRoute>
            }
          />

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* 404 page */}
          <Route
            path="*"
            element={
              <Suspense fallback={<PageLoader />}>
                <NotFoundPage />
              </Suspense>
            }
          />
        </Routes>
    </BrowserRouter>
  );
};
